import {
  QueryHookOptions,
  QueryResult,
  SubscriptionHookOptions,
  SubscriptionResult,
  useQuery,
  useSubscription,
} from '@apollo/client'
import styled from 'styled-components'

import { Nullable } from 'shared/utils/type-guards'

import ProjectOverviewCostCard from '~/views/components/ProjectOverview/components/ProjectOverviewCostCard'
import ProjectOverviewHoursCard from '~/views/components/ProjectOverview/components/ProjectOverviewHoursCard'
import * as ProjectOverviewHelper from '~/views/components/ProjectOverview/helpers/ProjectOverviewHelpers'
import { useCurrentUserContext } from '~/views/contexts/CurrentUserContext'
import {
  TimeTrackingSummaryProjectProfitabilitySummaryQuery,
  TimeTrackingSummaryProjectProfitabilitySummaryQueryVariables,
} from './ProjectProfitabilitySummaryQuery.generated'
import QueryDocument from './ProjectProfitabilitySummaryQuery.graphql'
import {
  TimeTrackingSummaryProjectUpdatedSubscription,
  TimeTrackingSummaryProjectUpdatedSubscriptionVariables,
} from './ProjectUpdateSubscription.generated'
import SubscriptionDocument from './ProjectUpdateSubscription.graphql'

import ProjectOverviewBillingCard from '../ProjectOverviewBillingCard'
import ProjectOverviewProfitCard from '../ProjectOverviewProfitCard'

const TimeTrackingSummaryContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  grid-area: ttsc;
  gap: ${({ theme }) => theme.spacing(3)};
`

export interface TimeTrackingSummaryProps {
  project?: {
    id: string
    organization?: Nullable<{
      id: string
      timeTrackingEnabled?: Nullable<boolean>
    }>
    projectBillRate?: Nullable<{
      id: string
      showHoursToCustomers?: Nullable<boolean>
      allottedHours?: Nullable<number>
    }>
  }
  stats?: {
    internalHours?: { estimated: number } | null
    totalTasks: number
    forecastedHours: number
    totalActualHours: number
    totalTasksWithUnestimatedHoursCount: number
    totalInternalLoggedBillableHours: number
  }
}

export default function TimeTrackingSummary({
  project,
  stats,
}: TimeTrackingSummaryProps) {
  const { currentUser } = useCurrentUserContext()
  const isInternalUser =
    ProjectOverviewHelper.userIsInternalToProjectOrganization({
      currentUser,
      project,
    })

  const advancedTTIsEnabled = ProjectOverviewHelper.advancedTTIsEnabled({
    project,
  })

  const { data: queryData, loading: queryLoading } =
    useProjectProfitabilitySummaryQuery({
      variables: { projectId: project?.id || '' },
      skip: !isInternalUser || !project?.id,
    })

  useProjectUpdateSubscription({
    variables: { projectId: project?.id || '' },
    skip: !isInternalUser || !project?.id,
  })

  if (!currentUser || !project || !advancedTTIsEnabled) {
    return null
  }

  return (
    <TimeTrackingSummaryContainer
      data-component-name={'time-tracking-summary-container'}
    >
      <ProjectOverviewHoursCard
        show={isInternalUser}
        loggedHours={stats?.totalActualHours}
        estimatedHours={stats?.internalHours?.estimated}
        totalTasksWithUnestimatedHoursCount={
          stats?.totalTasksWithUnestimatedHoursCount
        }
        forecastedHours={stats?.forecastedHours}
      />

      <ProjectOverviewCostCard
        projectHasTasks={
          stats?.totalTasks !== undefined && stats?.totalTasks > 0
        }
        show={isInternalUser}
        loading={queryLoading}
        accruedCost={queryData?.project?.profitabilitySummary?.totalCost}
        budgetedCost={queryData?.project?.profitabilitySummary?.budgetedCost}
        forecastedCost={
          queryData?.project?.profitabilitySummary?.forecastedCost
        }
      />

      <ProjectOverviewBillingCard
        isInternalUser={isInternalUser}
        loading={queryLoading}
        showHoursToCustomers={project?.projectBillRate?.showHoursToCustomers}
        accruedBilled={
          queryData?.project?.profitabilitySummary?.totalBilledAmount
        }
        billableHours={stats?.totalInternalLoggedBillableHours || 0}
        allottedHours={project?.projectBillRate?.allottedHours}
      />

      <ProjectOverviewProfitCard
        loading={queryLoading}
        totalProfit={queryData?.project?.profitabilitySummary?.totalProfit}
        show={isInternalUser}
      />
    </TimeTrackingSummaryContainer>
  )
}

const useProjectProfitabilitySummaryQuery = (
  options: QueryHookOptions<
    TimeTrackingSummaryProjectProfitabilitySummaryQuery,
    TimeTrackingSummaryProjectProfitabilitySummaryQueryVariables
  >,
): QueryResult<
  TimeTrackingSummaryProjectProfitabilitySummaryQuery,
  TimeTrackingSummaryProjectProfitabilitySummaryQueryVariables
> => {
  return useQuery<
    TimeTrackingSummaryProjectProfitabilitySummaryQuery,
    TimeTrackingSummaryProjectProfitabilitySummaryQueryVariables
  >(QueryDocument, options)
}

const useProjectUpdateSubscription = (
  options: SubscriptionHookOptions<
    TimeTrackingSummaryProjectUpdatedSubscription,
    TimeTrackingSummaryProjectUpdatedSubscriptionVariables
  >,
): SubscriptionResult<
  TimeTrackingSummaryProjectUpdatedSubscription,
  TimeTrackingSummaryProjectUpdatedSubscriptionVariables
> => {
  return useSubscription<
    TimeTrackingSummaryProjectUpdatedSubscription,
    TimeTrackingSummaryProjectUpdatedSubscriptionVariables
  >(SubscriptionDocument, options)
}
